import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';
import {
	TGpsrRegParseResponsePayload,
	TGpsrStore,
	TGpsrPlansResponsePayload,
	TGpsrItemsResponsePayload,
	TGpsrProductTypesResponsePayload,
	TGpsrRegulationsResponsePayload,
	TGetProductTypeReferencesResponsePayload,
	TGetProductTypeRegulationsGroupTypesResponsePayload,
	TGetProductTypeRegulationsReferencesResponsePayload,
	TGetRegulationsReferencesResponsePayload,
	TGetInvoicesListResponsePayload,
	TGpsrServiceStatusesResponsePayload,
} from './types';

// ====================================================:

const getGpsrState = (state: TStoreState): TGpsrStore => state.gpsr;
export const getGpsr = createSelector([getGpsrState], (gpsr: TGpsrStore) => gpsr);

// ====================================================:

export const getRegParserLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.regParserLoading,
);

// ====================================================:

export const getRegParser = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrRegParseResponsePayload => gpsr.regParser,
);

// ====================================================:

export const getRegParserError = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): string => gpsr.regParserError,
);

// ====================================================:

export const getRegisterLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.registerLoading,
);

// ====================================================:

export const getRegisterError = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): string => gpsr.registerError,
);

// ====================================================:

export const getGpsrPlans = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrPlansResponsePayload => gpsr.gpsrPlans,
);

// ====================================================:

export const getGpsrItems = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrItemsResponsePayload => gpsr.gpsrItems,
);

// ====================================================:

export const getGpsrItemsLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrItemsLoading,
);

// ====================================================:

export const getGpsrProductTypes = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrProductTypesResponsePayload => gpsr.gpsrProductTypes,
);

// ====================================================:

export const getGpsrProductTypesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrProductTypesLoading,
);

// ====================================================:

export const getGenerateInvoiceGpsrLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.generateInvoiceGpsrLoading,
);

// ====================================================:

export const getGpsrRegulations = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrRegulationsResponsePayload => gpsr.gpsrRegulations,
);

// ====================================================:

export const getGpsrRegulationsLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrRegulationsLoading,
);

// ====================================================:

export const getGpsrProductTypeReferences = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGetProductTypeReferencesResponsePayload => gpsr.gpsrProductTypeReferences,
);

// ====================================================:

export const getGpsrProductTypeReferencesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrProductTypeReferencesLoading,
);

// ====================================================:

export const getGpsrProductTypeRegulationsGroupTypes = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGetProductTypeRegulationsGroupTypesResponsePayload =>
		gpsr.gpsrProductTypeRegulationsGroupTypes,
);

// ====================================================:

export const getGpsrProductTypeRegulationsGroupTypesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrProductTypeRegulationsGroupTypesLoading,
);

// ====================================================:

export const getGpsrProductTypeRegulationsReferences = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGetProductTypeRegulationsReferencesResponsePayload =>
		gpsr.gpsrProductTypeRegulationsReferences,
);

// ====================================================:

export const getGpsrProductTypeRegulationsReferencesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrProductTypeRegulationsReferencesLoading,
);

// ====================================================:

export const getGpsrRegulationsReferences = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGetRegulationsReferencesResponsePayload => gpsr.gpsrRegulationsReferences,
);

// ====================================================:

export const getGpsrRegulationsReferencesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrRegulationsReferencesLoading,
);

// ====================================================:

export const getGpsrTechFileLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrTechFileLoading,
);

// ====================================================:

export const getGpsrDocFileLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrDocFileLoading,
);

// ====================================================:

export const getDownloadGpsrLastInvoiceLoading = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.downloadGpsrLastInvoiceLoading,
);

// ====================================================:

export const getGpsrInvoicesList = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGetInvoicesListResponsePayload => gpsr.getInvoicesList,
);

// ====================================================:

export const getGpsrInvoicesListLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.getInvoicesListLoading,
);

// ====================================================:

export const getDownloadGpsrInvoiceLoading = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.downloadGpsrInvoiceLoading,
);

// ====================================================:

export const getUpdateGpsrServiceSettingsLoading = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.updateGpsrServiceSettingsLoading,
);

// ====================================================:

export const getUpdateGpsrRemoveOnRenewalLoading = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.updateGpsrRemoveOnRenewalLoading,
);

// ====================================================:

export const getChangeGpsrStatusLoading = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.changeGpsrStatusLoading,
);

// ====================================================:

export const getGpsrServiceStatuses = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): TGpsrServiceStatusesResponsePayload => gpsr.gpsrServiceStatuses,
);

// ====================================================:

export const getGpsrServiceStatusesLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrServiceStatusesLoading,
);

// ====================================================:

export const getGpsrReferralLoad = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): boolean => gpsr.gpsrReferralLoading,
);

// ====================================================:

export const getGpsrReferralError = createSelector(
	[getGpsr],
	(gpsr: TGpsrStore): string => gpsr.gpsrReferralError,
);

// ====================================================:
